// studentapps/StudyTimer.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  IconButton,
  Chip
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Stop,
  Delete as DeleteIcon,
  Timer as TimerIcon,
  CorporateFare as CorporateIcon
} from '@mui/icons-material';

const StudyTimer = () => {
  const [time, setTime] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [sessions, setSessions] = useState([
    { id: 1, name: 'Math Study', duration: 3600, date: '2024-01-15' },
    { id: 2, name: 'Physics Revision', duration: 2700, date: '2024-01-14' },
    { id: 3, name: 'Chemistry Problems', duration: 1800, date: '2024-01-13' }
  ]);

  useEffect(() => {
    let interval = null;
    if (isActive) {
      interval = setInterval(() => {
        setTime(time => time + 1);
      }, 1000);
    } else if (!isActive && time !== 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isActive, time]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    setIsActive(true);
  };

  const handlePause = () => {
    setIsActive(false);
  };

  const handleStop = () => {
    setIsActive(false);
    if (sessionName && time > 0) {
      const session = {
        id: Date.now(),
        name: sessionName,
        duration: time,
        date: new Date().toISOString().split('T')[0]
      };
      setSessions([session, ...sessions]);
    }
    setTime(0);
    setSessionName('');
  };

  const deleteSession = (id) => {
    setSessions(sessions.filter(session => session.id !== id));
  };

  const totalStudyTime = sessions.reduce((total, session) => total + session.duration, 0);
  const averageSessionTime = sessions.length > 0 ? totalStudyTime / sessions.length : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="700" gutterBottom>
            Study Timer
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Track your study sessions and build consistency
          </Typography>
        </Box>
        <Chip 
          label="Powered by Zolang Nexora" 
          color="primary" 
          variant="outlined"
          icon={<CorporateIcon />}
        />
      </Box>

      <Grid container spacing={3}>
        {/* Timer */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <TimerIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              
              <Typography variant="h2" color="primary" gutterBottom sx={{ fontSize: { xs: '2.5rem', sm: '3.5rem' }, fontWeight: 700 }}>
                {formatTime(time)}
              </Typography>
              
              <TextField
                fullWidth
                label="Session Name"
                variant="outlined"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                sx={{ mb: 3, maxWidth: 300, mx: 'auto' }}
                placeholder="e.g., Calculus Revision"
                size="small"
              />

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                {!isActive ? (
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={handleStart}
                    size="large"
                    disabled={!sessionName}
                  >
                    Start
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<Pause />}
                    onClick={handlePause}
                    size="large"
                  >
                    Pause
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Stop />}
                  onClick={handleStop}
                  size="large"
                  disabled={time === 0}
                >
                  Stop & Save
                </Button>
              </Box>

              {isActive && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Currently studying: <strong>{sessionName}</strong>
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Study Statistics
              </Typography>
              <Grid container spacing={2} sx={{ textAlign: 'center' }}>
                <Grid item xs={4}>
                  <Typography variant="h4" color="primary" fontWeight="700">
                    {sessions.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sessions
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h4" color="secondary" fontWeight="700">
                    {formatTime(totalStudyTime).split(':')[0]}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Time
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h4" color="accent.main" fontWeight="700">
                    {formatTime(averageSessionTime).split(':')[1]}m
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Session
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Study Sessions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Study Sessions ({sessions.length})
              </Typography>
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {sessions.map((session) => (
                  <Paper key={session.id} sx={{ mb: 1, borderRadius: 2 }}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body1" fontWeight="500">
                              {session.name}
                            </Typography>
                            <Chip 
                              label={formatTime(session.duration)} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={`Date: ${session.date}`}
                      />
                      <IconButton
                        edge="end"
                        onClick={() => deleteSession(session.id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  </Paper>
                ))}
                {sessions.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No study sessions recorded yet. Start your first session!
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>

          {/* Study Tips */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Study Tips
              </Typography>
              <List dense>
                {[
                  'Use the Pomodoro technique (25 min study, 5 min break)',
                  'Take regular breaks to maintain focus',
                  'Set specific goals for each session',
                  'Review your notes after each session',
                  'Stay hydrated and maintain good posture'
                ].map((tip, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={`${index + 1}. ${tip}`} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudyTimer;